// src/components/auction/AuctionMantraManageTab.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AuctionMantraManageTab.css';
import TeamBadge from '../TeamBadge';
import Tooltip from '../Tooltip';
import {
  BarChart3,
  Shield,
  Trophy,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Layers,
  Users,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Siren,
} from 'lucide-react';

import {
  MANTRA_PRESETS,
  MANTRA_TOKEN_ORDER,
  ROLE_COMPATIBILITY_MAP,
  MANTRA_ROLE_THEME,
} from './MantraRoleConfiguration';

// Alias per compatibilità con il codice esistente
const MANTRA_ROLE_ORDER = MANTRA_TOKEN_ORDER;
const MANTRA_PRESET_COUNT = Object.keys(MANTRA_PRESETS).length;

// Reparti Mantra: raggruppamento ufficiale dei 12 ruoli (coerente con i colori
// delle barre e con il sistema Mantra di fantacalcio.it).
const MANTRA_REPARTI = [
  { key: 'P', label: 'Portieri',       roles: ['P'],                     color: '#f5c542' },
  { key: 'D', label: 'Difensori',      roles: ['Dc', 'Dd', 'Ds', 'B'],   color: '#22c55e' },
  { key: 'C', label: 'Centrocampisti', roles: ['E', 'M', 'C', 'W', 'T'], color: '#3b82f6' },
  { key: 'A', label: 'Attaccanti',     roles: ['A', 'Pc'],               color: '#ef4444' },
];

// ─── UTILITIES ────────────────────────────────────────────────────────────────

function v(x) { return Number(x || 0); }

function fmt(x) {
  const n = Number(x || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function splitRoleTokens(roleValue = '') {
  if (Array.isArray(roleValue)) return roleValue.map(r => String(r || '').trim()).filter(Boolean);
  return String(roleValue || '').trim()
    .split(/[/,|]+|\s+/)
    .map(p => p.trim())
    .filter(Boolean);
}

function normalizeMantraToken(token = '') {
  const map = { P:'P', DC:'Dc', DD:'Dd', DS:'Ds', B:'B', E:'E', M:'M', C:'C', W:'W', T:'T', A:'A', PC:'Pc' };
  return map[String(token || '').trim().toUpperCase()] || String(token || '').trim();
}

function extractRoleTokens(entry) {
  if (!entry) return [];
  const snap = entry.playerSnapshot || {};
  const candidates = [
    snap.mantraRole, snap.mantraRoles, snap.roleMantra, snap.mantra,
    entry.mantraRole, entry.mantraRoles, entry.roleMantra, entry.mantra,
    snap.roles, entry.roles, snap.subRole, entry.subRole,
    snap.role, entry.role,
  ];
  const source = candidates.find(c => Array.isArray(c) ? c.length > 0 : String(c || '').trim() !== '');
  if (!source) return [];
  return [...new Set(splitRoleTokens(source).map(normalizeMantraToken).filter(Boolean))];
}


function getRoleStyle(role = '') {
  const theme = MANTRA_ROLE_THEME[role] || MANTRA_ROLE_THEME.Dc;
  return { background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color };
}

function getFinanceBucket(roleValue = '') {
  const tokens = splitRoleTokens(roleValue).map(normalizeMantraToken);
  if (tokens.includes('P')) return 'P';
  if (tokens.some(t => ['Dc','Dd','Ds','B'].includes(t))) return 'D';
  if (tokens.some(t => ['E','M','C','W','T'].includes(t))) return 'C';
  if (tokens.some(t => ['A','Pc'].includes(t))) return 'A';
  const up = String(roleValue || '').trim().toUpperCase();
  return ['P','D','C','A'].includes(up) ? up : 'A';
}

// ─── NORMALIZZAZIONE GIOCATORE ────────────────────────────────────────────────

function normalizePlayer(entry, valuationDrafts = {}) {
  if (!entry) return null;
  const snap = entry.playerSnapshot || {};
  const id = String(entry.playerId ?? snap.id ?? entry.id ?? '');
  if (!id) return null;

  const name    = String(snap.name ?? entry.playerName ?? entry.name ?? '—');
  const team    = String(snap.team ?? entry.playerTeam ?? entry.team ?? '—');
  const roles   = extractRoleTokens(entry);
  const roleLabel = roles.length ? roles.join('/') : '—';
  const classicRole = String(snap.originalRole ?? snap.role ?? entry.originalRole ?? entry.role ?? '').trim();
  const financeBucket = getFinanceBucket(classicRole || roles[0] || '');
  const moduleBuckets = roles;

  const buyPrice = v(entry.credits ?? entry.buyPrice ?? entry.price ?? 0);
  const draftRaw = valuationDrafts?.[id];
  const valuation = draftRaw !== undefined && draftRaw !== null && String(draftRaw).trim() !== ''
    ? v(draftRaw)
    : v(entry.valuation ?? snap.value ?? snap.valuation ?? 0);
  const delta = valuation - buyPrice;

  const goals       = v(snap.goals ?? entry.goals);
  const assists     = v(snap.assists ?? entry.assists);
  const ycards      = v(snap.ycards ?? entry.ycards);
  const rcards      = v(snap.rcards ?? entry.rcards);
  const owngoals    = v(snap.owngoals ?? entry.owngoals);
  const penalties   = v(snap.penalties ?? entry.penalties);
  const mpenalties  = v(snap.mpenalties ?? entry.mpenalties);
  const saves       = v(snap.saves ?? snap.penaltiesSaved ?? snap.savedPenalties ?? entry.saves ?? 0);
  const gotgoals    = v(snap.gotgoals ?? entry.gotgoals);
  const caps        = v(snap.caps ?? entry.caps ?? 0);
  const markavg     = v(snap.markavg ?? entry.markavg ?? 0);
  const fmarkavg    = v(snap.fmarkavg ?? entry.fmarkavg ?? 0);

  const isKeeper = financeBucket === 'P' || roles.includes('P');
  const offensiveImpact  = isKeeper ? 0 : goals * 4 + assists * 3 + penalties * 2;
  const disciplineImpact = ycards * 0.3 + rcards * 2 + owngoals * 1.5 + mpenalties * 2.5;
  const keeperImpact     = isKeeper ? saves * 4 - gotgoals * 0.35 - mpenalties * 1.5 : 0;
  const netImpact        = offensiveImpact + keeperImpact - disciplineImpact;

  return {
    id, name, team, roles, roleLabel, financeBucket, moduleBuckets,
    buyPrice, valuation, delta,
    goals, assists, ycards, rcards, owngoals, penalties, mpenalties, saves, gotgoals,
    caps, markavg, fmarkavg,
    offensiveImpact, disciplineImpact, keeperImpact, netImpact, isKeeper,
  };
}

// ─── ANALISI MODULI ──────────────────────────────────────────────────────────
// Matching bipartito: conta quanti slot del modulo (espressi come array di token accettati)
// possono essere coperti dai giocatori disponibili.

function matchSlots(players, flatSlots) {
  if (!flatSlots.length || !players.length) {
    return { matched: 0, matchedMask: flatSlots.map(() => false), slotPlayers: flatSlots.map(() => null) };
  }

  const slotData = flatSlots.map((accepted, idx) => ({
    idx,
    candidates: players
      .map((p, pi) => accepted.some(tok => p.roles.includes(tok)) ? pi : -1)
      .filter(pi => pi >= 0),
  })).sort((a, b) => a.candidates.length - b.candidates.length);

  const matchPlayer = new Array(players.length).fill(-1);

  function augment(si, seen) {
    for (const pi of slotData[si].candidates) {
      if (seen[pi]) continue;
      seen[pi] = true;
      const prev = matchPlayer[pi];
      if (prev === -1 || augment(prev, seen)) { matchPlayer[pi] = si; return true; }
    }
    return false;
  }

  let matched = 0;
  for (let s = 0; s < slotData.length; s++) {
    if (augment(s, new Array(players.length).fill(false))) matched++;
  }

  const matchedOriginalIdxs = new Set();
  const slotPlayers = flatSlots.map(() => null);
  matchPlayer.forEach((si, pi) => {
    if (si !== -1) {
      const originalIdx = slotData[si].idx;
      matchedOriginalIdxs.add(originalIdx);
      slotPlayers[originalIdx] = players[pi];
    }
  });

  return { matched, matchedMask: flatSlots.map((_, i) => matchedOriginalIdxs.has(i)), slotPlayers };
}

// Un titolare "a rischio" è assegnato a uno slot ma risulta in panchina (o assente)
// nella probabile formazione FantaMaster: la copertura del modulo è tecnicamente
// ok, ma quello slot potrebbe non prendere voto alla prossima giornata — SALVO
// che in rosa ci sia già un altro giocatore, non usato in questo modulo, con
// buona probabilità di scendere in campo e ruolo compatibile con quello slot:
// in quel caso il modulo resta effettivamente coperto in caso di assenza.
function isAtPlayRisk(player, playProbabilityById) {
  const entry = playProbabilityById?.[String(player?.id)];
  return Boolean(entry?.status) && entry.status !== 'starter';
}

function hasGoodPlayProbability(player, playProbabilityById) {
  const entry = playProbabilityById?.[String(player?.id)];
  return entry?.status === 'starter';
}

function buildModuleCoverage(players, playProbabilityById = {}) {
  return Object.entries(MANTRA_PRESETS).map(([key, preset]) => {
    const flatSlots  = preset.rows.flatMap(row => row.slots);
    const totalSlots = flatSlots.length;
    const { matched, matchedMask, slotPlayers } = matchSlots(players, flatSlots);
    const coverage   = totalSlots > 0 ? Math.round((matched / totalSlots) * 100) : 0;
    const playable   = matched === totalSlots;
    const missing    = totalSlots - matched;

    // Riserve per QUESTO modulo: chiunque in rosa non sia già titolare in uno slot.
    const usedIds  = new Set(slotPlayers.filter(Boolean).map(p => p.id));
    const reserves = players.filter(p => !usedIds.has(p.id));
    const backupUsedIds = new Set();

    const riskCovered   = []; // titolare a rischio, ma con un sostituto affidabile in rosa
    const riskUncovered = []; // titolare a rischio: schierarlo è obbligato, nessun sostituto affidabile
    const thinDepth     = []; // titolare ok, ma l'unico sostituto per quel ruolo è a sua volta a rischio

    flatSlots.forEach((options, i) => {
      const starter = slotPlayers[i];
      if (!starter) return;

      const compatibleReserves = reserves.filter(p =>
        options.some(t => p.roles.includes(t)) ||
        options.some(t => (ROLE_COMPATIBILITY_MAP[t] || []).some(r => p.roles.includes(r)))
      );

      if (isAtPlayRisk(starter, playProbabilityById)) {
        const candidates = compatibleReserves.filter(p => !backupUsedIds.has(p.id) && hasGoodPlayProbability(p, playProbabilityById));
        const direct = candidates.find(p => options.some(t => p.roles.includes(t)));
        const withMalus = !direct ? candidates.find(p => options.some(t => (ROLE_COMPATIBILITY_MAP[t] || []).some(r => p.roles.includes(r)))) : null;
        const backup = direct || withMalus;

        if (backup) {
          backupUsedIds.add(backup.id);
          riskCovered.push({ starter, backup, malus: Boolean(withMalus) });
        } else {
          riskUncovered.push({ starter });
        }
      } else if (compatibleReserves.length === 1 && isAtPlayRisk(compatibleReserves[0], playProbabilityById)) {
        thinDepth.push({ starter, backup: compatibleReserves[0] });
      }
    });

    let maskOffset = 0;
    const rows = preset.rows.map(row => {
      const slotMatches = matchedMask.slice(maskOffset, maskOffset + row.slots.length);
      const rowSlotPlayers = slotPlayers.slice(maskOffset, maskOffset + row.slots.length);
      maskOffset += row.slots.length;
      const required  = row.slots.length;
      const available = slotMatches.filter(Boolean).length;
      return {
        key:             row.key,
        title:           row.title,
        subtitle:        row.subtitle,
        slots:           row.slots,
        required,
        available,
        missing:         required - available,
        slotMatchedArray: slotMatches,
        slotPlayers:     rowSlotPlayers,
      };
    });

    return {
      key, label: preset.label, rows, coverage, playable, missing,
      riskCovered,
      riskUncovered,
      thinDepth,
      riskCount: riskUncovered.length,
    };
  }).sort((a, b) => {
    if (a.playable !== b.playable) return a.playable ? -1 : 1;
    return b.coverage - a.coverage;
  });
}

// ─── ANALISI COPERTURA RUOLI ─────────────────────────────────────────────────

function buildRoleCoverage(players) {
  const counts = Object.fromEntries(MANTRA_ROLE_ORDER.map(r => [r, 0]));
  players.forEach(p => p.roles.forEach(r => { if (counts[r] !== undefined) counts[r]++; }));

  const reparti = MANTRA_REPARTI.map(rep => ({
    ...rep,
    total: rep.roles.length,
    covered: rep.roles.filter(r => counts[r] > 0).length,
  }));

  return {
    byRole: MANTRA_ROLE_ORDER.map(r => ({ role: r, count: counts[r] })),
    reparti,
    criticalGaps: MANTRA_ROLE_ORDER.filter(r => counts[r] === 0),
  };
}

// ─── ANALISI COMPATIBILITÀ SOSTITUZIONI ──────────────────────────────────────

// Il livello è guidato soprattutto dai titolari DIRETTI, non dal totale con
// i "jolly con malus": un solo diretto resta un punto di rottura singolo — se
// si infortuna, in campo si va solo con il malus −1 — quindi non è MAI
// "Coperto", per quanti jolly di scorta ci siano. Con 2 diretti il reparto è
// "Coperto" ma mai "Solido"; tra 3 e 5 diretti è "Solido"; oltre 5 si è
// "Abbondante" (più titolari diretti di quanti se ne possano schierare).
function computeCompatStatus(directCount, compatCount) {
  const total = directCount + compatCount;
  if (total === 0) return 'Scoperto';

  if (directCount === 0) return compatCount >= 2 ? 'Fragile' : 'Critico';
  if (directCount === 1) return compatCount >= 1 ? 'Fragile' : 'Critico';
  if (directCount === 2) return 'Coperto';
  if (directCount <= 5) return 'Solido';
  return 'Abbondante';
}

function buildCompatMatrix(players) {
  return MANTRA_ROLE_ORDER.map(role => {
    const direct     = players.filter(p => p.roles.includes(role));
    const compatible = players.filter(p => !p.roles.includes(role) && ROLE_COMPATIBILITY_MAP[role]?.some(cr => p.roles.includes(cr)));
    const total      = direct.length + compatible.length;
    const status     = computeCompatStatus(direct.length, compatible.length);

    return {
      role,
      direct: direct.map(p => ({ id: p.id, name: p.name, team: p.team, roles: p.roles })),
      compatible: compatible.map(p => ({ id: p.id, name: p.name, team: p.team, roles: p.roles })),
      directCount: direct.length,
      compatCount: compatible.length,
      total,
      status,
      compatRoles: ROLE_COMPATIBILITY_MAP[role] || [],
    };
  });
}

// ─── CRUSCOTTO AVVISI AGGREGATO ───────────────────────────────────────────────
// Raccoglie in un unico elenco i segnali già calcolati dalle altre sezioni
// (copertura ruoli, moduli/rischio panchina, matrice compatibilità, portieri,
// flessibilità moduli) così da non doverli scovare sezione per sezione.

function buildAggregatedAlerts(players, moduleCoverage) {
  const alerts = [];
  const roleCoverage = buildRoleCoverage(players);
  const compatMatrix = buildCompatMatrix(players);
  const bestModule = moduleCoverage.find(m => m.playable) || moduleCoverage[0] || null;

  if (roleCoverage.criticalGaps.length) {
    alerts.push({
      id: 'role-gaps',
      severity: 'critical',
      title: 'Ruoli mai coperti',
      description: `Nessun giocatore in rosa copre ${roleCoverage.criticalGaps.join(', ')}: qualsiasi modulo che li richiede è irraggiungibile.`,
    });
  }

  const structuralCritical = compatMatrix.filter(r => r.status === 'Critico' && !roleCoverage.criticalGaps.includes(r.role));
  if (structuralCritical.length) {
    alerts.push({
      id: 'role-critical',
      severity: 'warning',
      title: 'Ruoli con un solo titolare diretto',
      description: `${structuralCritical.map(r => r.role).join(', ')}: un solo giocatore diretto e nessun jolly di riserva in rosa. Un'assenza ti costringe al malus o a cambiare modulo.`,
    });
  }

  if (bestModule) {
    if (bestModule.riskUncovered.length) {
      alerts.push({
        id: 'starters-uncovered',
        severity: 'critical',
        title: `Titolari a rischio senza sostituto (${bestModule.label})`,
        description: `${bestModule.riskUncovered.map(r => r.starter.name).join(', ')} rischia/rischiano la panchina alla prossima giornata e non hai un sostituto affidabile in rosa per quel ruolo.`,
      });
    }

    const riskCoveredMalus = bestModule.riskCovered.filter(r => r.malus);
    if (riskCoveredMalus.length) {
      alerts.push({
        id: 'starters-malus',
        severity: 'warning',
        title: `Coperture solo con malus (${bestModule.label})`,
        description: `Se ${riskCoveredMalus.map(r => r.starter.name).join(', ')} salta/saltano la panchina, l'unico sostituto disponibile entra fuori ruolo (malus −1).`,
      });
    }

    if (bestModule.thinDepth.length) {
      alerts.push({
        id: 'thin-depth',
        severity: 'warning',
        title: `Riserve a rischio (${bestModule.label})`,
        description: `${bestModule.thinDepth.map(r => r.backup.name).join(', ')}: unica riserva per il proprio ruolo, ma rischia a sua volta la panchina.`,
      });
    }
  }

  const keeperCount = players.filter(p => p.isKeeper).length;
  if (keeperCount < 2) {
    alerts.push({
      id: 'keepers',
      severity: keeperCount === 0 ? 'critical' : 'warning',
      title: keeperCount === 0 ? 'Nessun portiere in rosa' : 'Un solo portiere in rosa',
      description: keeperCount === 0
        ? 'Nessun modulo è giocabile senza un portiere in rosa.'
        : 'In caso di indisponibilità del titolare non hai un secondo portiere per coprire il ruolo.',
    });
  }

  const playableCount = moduleCoverage.filter(m => m.playable).length;
  if (playableCount <= 2) {
    alerts.push({
      id: 'few-modules',
      severity: playableCount === 0 ? 'critical' : 'warning',
      title: playableCount === 0 ? 'Nessun modulo giocabile' : 'Pochi moduli giocabili',
      description: `Solo ${playableCount} modulo/i su ${MANTRA_PRESET_COUNT} è/sono schierabile/i con la rosa attuale: poca flessibilità in caso di assenze o cambi modulo forzati.`,
    });
  }

  const severityRank = { critical: 0, warning: 1 };
  return alerts.sort((a, b) => severityRank[a.severity] - severityRank[b.severity]);
}

// ─── INSIGHTS ────────────────────────────────────────────────────────────────

function buildInsights(players) {
  const sorted  = [...players].sort((a, b) => b.delta - a.delta);
  const byBonus = [...players].sort((a, b) => (b.offensiveImpact + b.keeperImpact) - (a.offensiveImpact + a.keeperImpact));
  const byRisk  = [...players].sort((a, b) => b.disciplineImpact - a.disciplineImpact);
  const byCaps  = [...players].sort((a, b) => b.caps - a.caps);

  return {
    topWinners:  sorted.filter(p => p.delta > 0).slice(0, 5),
    topLosers:   sorted.filter(p => p.delta < 0).reverse().slice(0, 5),
    bonusLeaders: byBonus.filter(p => !p.isKeeper).slice(0, 5),
    riskLeaders:  byRisk.filter(p => p.disciplineImpact > 0).slice(0, 5),
    mostPlayed:   byCaps.slice(0, 5),
  };
}

// ─── ANALISI FINANZIARIA ─────────────────────────────────────────────────────

function buildFinance(players) {
  const cols = { P: [], D: [], C: [], A: [] };
  let cost = 0, value = 0;

  players.forEach(p => {
    const b = cols[p.financeBucket] ? p.financeBucket : 'A';
    cols[b].push(p);
    cost  += p.buyPrice;
    value += p.valuation;
  });

  Object.keys(cols).forEach(k => cols[k].sort((a, b) => b.delta - a.delta));

  const delta = value - cost;
  const positive = players.filter(p => p.delta >= 0).length;
  const negative = players.length - positive;
  const efficiency = cost > 0 ? (value / cost) * 100 : 0;

  return { cols, cost, value, delta, positive, negative, efficiency, avgCost: players.length ? cost / players.length : 0 };
}

// ─── COMPONENTI UI ────────────────────────────────────────────────────────────

function RoleChip({ role, size = 'md' }) {
  const style = getRoleStyle(role);
  return <span className={`mm-role-chip mm-role-chip--${size}`} style={style}>{role}</span>;
}

function RoleChipGroup({ roles = [] }) {
  if (!roles.length) return <span className="mm-muted">—</span>;
  return (
    <div className="mm-role-chip-group">
      {roles.map(r => <RoleChip key={r} role={r} size="sm" />)}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Abbondante: { cls: 'abundant', icon: <Layers size={12} /> },
    Solido:   { cls: 'positive', icon: <CheckCircle2 size={12} /> },
    Coperto:  { cls: 'ok',       icon: <CheckCircle2 size={12} /> },
    Fragile:  { cls: 'warn',     icon: <AlertTriangle size={12} /> },
    Critico:  { cls: 'danger',   icon: <AlertTriangle size={12} /> },
    Scoperto: { cls: 'danger',   icon: <XCircle size={12} /> },
  };
  const { cls, icon } = map[status] || { cls: 'ok', icon: null };
  return <span className={`mm-status-badge mm-status-badge--${cls}`}>{icon}{status}</span>;
}

function DeltaBadge({ delta }) {
  if (Math.abs(delta) < 0.01) return <span className="mm-delta mm-delta--neutral"><Minus size={12} />0</span>;
  if (delta > 0) return <span className="mm-delta mm-delta--pos"><ArrowUpRight size={12} />+{fmt(delta)}</span>;
  return <span className="mm-delta mm-delta--neg"><ArrowDownRight size={12} />{fmt(delta)}</span>;
}

function SectionHead({ title, sub, badge, icon }) {
  return (
    <div className="mm-section-head">
      <div>
        <h3 className="mm-section-title">{title}</h3>
        {sub && <p className="mm-section-sub">{sub}</p>}
      </div>
      {badge && (
        <div className="mm-section-badge">
          {icon}
          <span>{badge}</span>
        </div>
      )}
    </div>
  );
}

// ─── SEZIONE: CRUSCOTTO AVVISI ────────────────────────────────────────────────

const ALERT_SEVERITY_META = {
  critical: { icon: <XCircle size={14} />, cls: 'danger', label: 'Critico' },
  warning:  { icon: <AlertTriangle size={14} />, cls: 'warn', label: 'Avviso' },
};

function AlertsDashboardSection({ players, moduleCoverage }) {
  const alerts = useMemo(() => buildAggregatedAlerts(players, moduleCoverage), [players, moduleCoverage]);
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount  = alerts.filter(a => a.severity === 'warning').length;

  return (
    <section className="mm-section glass">
      <SectionHead
        title="Cruscotto Avvisi"
        sub="Tutti i segnali critici della rosa (ruoli, moduli, sostituzioni, portieri) in un unico posto."
        badge={alerts.length ? `${criticalCount} critici · ${warningCount} avvisi` : 'Tutto ok'}
        icon={<Siren size={14} />}
      />

      {alerts.length === 0 ? (
        <div className="mm-alert mm-alert--ok">
          <CheckCircle2 size={16} />
          <div><strong>Nessun allarme.</strong> Rosa, moduli e sostituzioni sono in buono stato.</div>
        </div>
      ) : (
        <div className="mm-alerts-grid">
          {alerts.map(a => {
            const meta = ALERT_SEVERITY_META[a.severity];
            return (
              <div key={a.id} className={`mm-alert-card mm-alert-card--${meta.cls}`}>
                <div className="mm-alert-card__head">
                  {meta.icon}
                  <span>{meta.label}</span>
                </div>
                <strong>{a.title}</strong>
                <p>{a.description}</p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── SEZIONE: COPERTURA RUOLI ─────────────────────────────────────────────────

function RoleCoverageSection({ players }) {
  const { byRole, reparti, criticalGaps } = useMemo(() => buildRoleCoverage(players), [players]);

  const maxCount = Math.max(...byRole.map(r => r.count), 1);

  return (
    <section className="mm-section glass">
      <SectionHead
        title="Copertura Ruoli Mantra"
        sub={reparti.map(rep => `${rep.label} ${rep.covered}/${rep.total}`).join(' • ')}
        badge={criticalGaps.length ? `${criticalGaps.length} ruoli scoperti` : 'Ottima copertura'}
        icon={<Trophy size={14} />}
      />

      {criticalGaps.length > 0 && (
        <div className="mm-alert mm-alert--warn">
          <AlertTriangle size={16} />
          <div>
            <strong>Ruoli non coperti:</strong>{' '}
            {criticalGaps.map(r => (
              <RoleChip key={r} role={r} size="xs" />
            ))}
          </div>
        </div>
      )}

      <div className="mm-role-coverage-grid">
        {byRole.map(({ role, count }) => {
          const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
          const theme = MANTRA_ROLE_THEME[role] || MANTRA_ROLE_THEME.Dc;
          return (
            <div key={role} className={`mm-role-coverage-item ${count === 0 ? 'is-empty' : ''}`}>
              <div className="mm-role-coverage-bar-wrap">
                <div
                  className="mm-role-coverage-bar"
                  style={{ height: `${Math.max(pct, 4)}%`, background: theme.color }}
                />
              </div>
              <div className="mm-role-coverage-label">
                <span className="mm-role-coverage-count" style={{ color: count === 0 ? 'var(--muted)' : theme.color }}>
                  {count}
                </span>
                <RoleChip role={role} size="xs" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mm-role-coverage-legend">
        {reparti.map(rep => (
          <div key={rep.key} className="mm-legend-item">
            <span className="mm-legend-dot" style={{ background: rep.color }} />
            <span>{rep.label} ({rep.roles.join(', ')})</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── SEZIONE: COPERTURA MODULI ────────────────────────────────────────────────

function ModuleCard({ module }) {
  const [expanded, setExpanded] = useState(false);

  const riskCoveredDirect = module.riskCovered.filter(r => !r.malus);
  const riskCoveredMalus  = module.riskCovered.filter(r => r.malus);

  const hasNotes = module.riskCovered.length > 0 || module.riskUncovered.length > 0 || module.thinDepth.length > 0;
  const severity = module.riskUncovered.length > 0
    ? 'danger'
    : (module.thinDepth.length > 0 || riskCoveredMalus.length > 0)
      ? 'warn'
      : riskCoveredDirect.length > 0
        ? 'ok'
        : null;

  const summaryParts = [];
  if (module.riskUncovered.length) {
    summaryParts.push(`${module.riskUncovered.length === 1 ? 'Un titolare a rischio reale' : `${module.riskUncovered.length} titolari a rischio reale`}`);
  }
  if (module.thinDepth.length) {
    summaryParts.push(`${module.thinDepth.length === 1 ? '1 riserva a rischio' : `${module.thinDepth.length} riserve a rischio`}`);
  }
  if (riskCoveredMalus.length) {
    summaryParts.push(`${riskCoveredMalus.length === 1 ? 'Coperto solo con malus' : `${riskCoveredMalus.length} coperture solo con malus (−1)`}`);
  }
  if (riskCoveredDirect.length) {
    summaryParts.push('Coperto anche in caso di assenza');
  }

  return (
    <div className={`mm-module-card ${module.playable ? 'is-playable' : 'is-partial'}`}>
      <div className="mm-module-head">
        <div className="mm-module-label">
          <strong>{module.label}</strong>
          <span>{module.playable ? 'Schierabile ✓' : `Mancano ${module.missing}`}</span>
        </div>
        <div className="mm-module-pct" style={module.playable ? { color: '#4ade80' } : {}}>
          {module.coverage}%
        </div>
      </div>

      <div className="mm-module-track">
        <div
          className="mm-module-fill"
          style={{
            width: `${module.coverage}%`,
            background: module.playable ? '#4ade80' : 'var(--primary)',
          }}
        />
      </div>

      {hasNotes && (
        <button
          type="button"
          className={`mm-module-risk-toggle mm-module-risk-toggle--${severity}`}
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
        >
          {severity === 'ok' ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
          <span>{summaryParts.join(' • ')}</span>
          <span className="mm-module-risk-toggle__chevron">{expanded ? '−' : '+'}</span>
        </button>
      )}

      {expanded && (
        <div className="mm-module-risk-detail">
          {module.riskUncovered.map(({ starter }) => (
            <div key={`u-${starter.id}`} className="mm-module-risk-line mm-module-risk-line--danger">
              <AlertTriangle size={12} />
              <span>
                Sei costretto a schierare <strong>{starter.name}</strong>: rischia la panchina e non c'è un sostituto affidabile in rosa per quel ruolo.
              </span>
            </div>
          ))}

          {module.thinDepth.map(({ starter, backup }) => (
            <div key={`t-${starter.id}`} className="mm-module-risk-line mm-module-risk-line--warn">
              <AlertTriangle size={12} />
              <span>
                <strong>{starter.name}</strong> gioca regolarmente, ma l'unico sostituto in panchina per quel ruolo, <strong>{backup.name}</strong>, rischia a sua volta la panchina.
              </span>
            </div>
          ))}

          {module.riskCovered.map(({ starter, backup, malus }) => (
            <div key={`c-${starter.id}`} className={`mm-module-risk-line ${malus ? 'mm-module-risk-line--warn' : 'mm-module-risk-line--ok'}`}>
              {malus ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
              <span>
                {malus
                  ? <><strong>{starter.name}</strong> rischia la panchina: sei coperto solo da <strong>{backup.name}</strong> (90%, ruolo assimilato, malus −1).</>
                  : <><strong>{starter.name}</strong> rischia la panchina, ma sei coperto da <strong>{backup.name}</strong> (90%).</>}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mm-module-rows">
        {module.rows.map(row => (
          <div key={row.key} className="mm-module-row">
            <div className="mm-module-row-slots">
              {row.slots.map((slot, i) => {
                const filled = row.slotMatchedArray ? row.slotMatchedArray[i] : i < row.available;
                return (
                  <Tooltip
                    as="div"
                    key={i}
                    className={`mm-module-slot ${filled ? 'is-filled' : 'is-empty'}`}
                    content={slot.join('/')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}
                  >
                    <span style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: filled ? '#fff' : 'var(--muted)',
                      letterSpacing: 0,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}>
                      {slot.join('/')}
                    </span>
                  </Tooltip>
                );
              })}
            </div>
            <span className="mm-module-row-count">{row.available}/{row.required}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModuleCoverageSection({ players, playProbabilityById = {} }) {
  const modules = useMemo(() => buildModuleCoverage(players, playProbabilityById), [players, playProbabilityById]);
  const playable = modules.filter(m => m.playable).length;
  const atRisk = modules.filter(m => m.playable && m.riskCount > 0).length;
  const best = modules[0];

  return (
    <section className="mm-section glass">
      <SectionHead
        title="Moduli Giocabili"
        sub={best ? `Miglior modulo: ${best.label} (${best.coverage}%)${atRisk ? ` • ${atRisk} moduli giocabili con titolari a rischio panchina` : ''}` : 'Nessun dato'}
        badge={`${playable} / ${MANTRA_PRESET_COUNT} moduli`}
        icon={<Layers size={14} />}
      />

      <div className="mm-module-grid">
        {modules.map(m => <ModuleCard key={m.key} module={m} />)}
      </div>
    </section>
  );
}

// ─── SEZIONE: MATRICE COMPATIBILITÀ ──────────────────────────────────────────

// Per un giocatore, i moduli in cui almeno uno dei suoi ruoli è accettato direttamente da uno slot
function getPlayerFreeFormations(playerRoles) {
  return Object.values(MANTRA_PRESETS)
    .filter(preset => {
      const slotRoles = new Set(preset.rows.flatMap(r => r.slots.flatMap(s => s)));
      return playerRoles.some(r => slotRoles.has(r));
    })
    .map(preset => preset.label);
}

function PlayerChip({ p, withMalus, subRole }) {
  const [open, setOpen] = useState(false);
  const freeFormations = useMemo(() => getPlayerFreeFormations(p.roles), [p.roles]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div className={`mm-compat-name-chip${withMalus ? ' mm-compat-name-chip--jolly' : ''}`}>
        <TeamBadge team={p.team} size={20} />
        <span>{p.name}</span>
        {withMalus && subRole && <RoleChip role={subRole} size="xs" />}
        {withMalus && <span style={{ fontSize: 10, color: '#f87171', fontWeight: 800 }}>−1</span>}
        <Tooltip
          as="button"
          type="button"
          onClick={() => setOpen(o => !o)}
          content="Ruoli ammessi come prima riserva"
          style={{
            marginLeft: 'auto',
            padding: '1px 6px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: open ? 'var(--primary)' : 'var(--card-solid)',
            color: open ? '#fff' : 'var(--muted)',
            fontSize: 10,
            fontWeight: 700,
            cursor: 'pointer',
            lineHeight: 1.6,
          }}
        >
          ▸
        </Tooltip>
      </div>
      {open && (
        <div style={{ paddingLeft: 10, display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Ruoli:</span>
            <RoleChipGroup roles={p.roles} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Senza malus in:</span>
            {freeFormations.length === 0
              ? <span style={{ fontSize: 11, color: '#f87171' }}>nessun modulo</span>
              : freeFormations.map(f => (
                  <span key={f} style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 6, background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.28)', color: '#4ade80' }}>{f}</span>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function CompatCard({ row }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mm-compat-card">
      <div className="mm-compat-head">
        <RoleChip role={row.role} />
        <StatusBadge status={row.status} />
        <button
          className="mm-compat-toggle"
          type="button"
          onClick={() => setExpanded(e => !e)}
          aria-label={expanded ? 'Comprimi' : 'Espandi'}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>

      <div className="mm-compat-stats">
        <div>
          <span>Diretti</span>
          <strong style={{ color: MANTRA_ROLE_THEME[row.role]?.color }}>{row.directCount}</strong>
        </div>
        <div>
          <span>Con malus</span>
          <strong>{row.compatCount}</strong>
        </div>
        <div>
          <span>Tot</span>
          <strong>{row.total}</strong>
        </div>
      </div>

      {expanded && (
        <div className="mm-compat-detail">
          <div className="mm-compat-block">
            <span className="mm-compat-block-label">Stessa posizione — nessun malus</span>
            {row.direct.length === 0
              ? <span className="mm-compat-empty">Nessuno in rosa</span>
              : <div className="mm-compat-names">
                  {row.direct.map(p => <PlayerChip key={p.id} p={p} withMalus={false} />)}
                </div>
            }
          </div>

          <div className="mm-compat-block">
            <span className="mm-compat-block-label">Giocatori compatibili con malus</span>
            {row.compatible.length === 0
              ? <span className="mm-compat-empty">Nessuno in rosa</span>
              : <div className="mm-compat-names">
                  {row.compatible.map(p => {
                    const subRole = p.roles.find(r => row.compatRoles.includes(r)) || p.roles[0];
                    return <PlayerChip key={p.id} p={p} withMalus subRole={subRole} />;
                  })}
                </div>
            }
          </div>
        </div>
      )}
    </div>
  );
}

function CompatSection({ players }) {
  const matrix = useMemo(() => buildCompatMatrix(players), [players]);
  const critical = matrix.filter(r => r.status === 'Scoperto' || r.status === 'Critico');

  return (
    <section className="mm-section glass">
      <SectionHead
        title="Compatibilità Sostituzioni"
        sub="Chi copre ogni ruolo in caso di assenza. I jolly entrano con malus −1."
        badge={critical.length ? `${critical.length} ruoli critici` : 'Copertura OK'}
        icon={<Shield size={14} />}
      />

      {critical.length > 0 && (
        <div className="mm-alert mm-alert--danger">
          <XCircle size={16} />
          <div>
            <strong>Attenzione:</strong> {critical.map(r => r.role).join(', ')} {critical.length === 1 ? 'è scoperto' : 'sono scoperti o critici'}.
          </div>
        </div>
      )}

      <div className="mm-compat-grid">
        {matrix.map(row => <CompatCard key={row.role} row={row} />)}
      </div>
    </section>
  );
}

// ─── SEZIONE: ANALISI FINANZIARIA ─────────────────────────────────────────────

function FinanceSection({ roster, valuationDrafts, onDraftChange, onCommit, savingId, readOnly = false }) {
  const players = useMemo(() => roster.map(e => normalizePlayer(e, valuationDrafts)).filter(Boolean), [roster, valuationDrafts]);
  const finance = useMemo(() => buildFinance(players), [players]);

  const colMeta = [
    { key: 'P', label: 'Portieri' },
    { key: 'D', label: 'Difensori' },
    { key: 'C', label: 'Centrocampisti' },
    { key: 'A', label: 'Attaccanti' },
  ];

  return (
    <section className="mm-section glass">
      <SectionHead
        title="Analisi Finanziaria"
        sub="Valutazione di mercato, plusvalenze e minusvalenze per ogni giocatore."
        badge="Plusvalenze"
        icon={<BarChart3 size={14} />}
      />

      <div className="mm-finance-kpis">
        <div className="mm-finance-kpi">
          <span>Costo totale</span>
          <strong>{fmt(finance.cost)} cr</strong>
        </div>
        <div className="mm-finance-kpi">
          <span>Valore stimato</span>
          <strong>{fmt(finance.value)} cr</strong>
        </div>
        <div className={`mm-finance-kpi ${finance.delta >= 0 ? 'is-positive' : 'is-negative'}`}>
          <span>Saldo netto</span>
          <strong>{finance.delta >= 0 ? '+' : ''}{fmt(finance.delta)} cr</strong>
        </div>
        <div className="mm-finance-kpi">
          <span>Efficienza budget</span>
          <strong>{fmt(finance.efficiency)}%</strong>
        </div>
        <div className="mm-finance-kpi is-positive">
          <span>Sopra acquisto</span>
          <strong>{finance.positive}</strong>
        </div>
        <div className="mm-finance-kpi is-negative">
          <span>Sotto acquisto</span>
          <strong>{finance.negative}</strong>
        </div>
      </div>

      <div className="mm-finance-grid">
        {colMeta.map(col => (
          <div key={col.key} className="mm-finance-col">
            <div className={`mm-finance-col-head mm-finance-col-head--${col.key}`}>{col.label}</div>
            <div className="mm-finance-list">
              {(finance.cols[col.key] || []).map(player => {
                const inputKey = String(player.id);
                const inputVal = valuationDrafts?.[inputKey] ?? '';
                return (
                  <div key={player.id} className="mm-finance-player">
                    <div className="mm-finance-player-top">
                      <TeamBadge team={player.team} size={20} />
                      <Tooltip as="span" className="mm-finance-player-name" content={player.name}>{player.name}</Tooltip>
                      <RoleChipGroup roles={player.roles} />
                    </div>
                    <div className="mm-finance-player-meta">
                      <span className="mm-finance-player-buy">Acq. {fmt(player.buyPrice)}</span>
                      <Tooltip as="span" content={readOnly ? 'Modalità osservazione — sola lettura' : undefined}>
                        <input
                          className="mm-finance-input"
                          type="number"
                          min="0"
                          step="1"
                          placeholder="Valore"
                          value={inputVal}
                          disabled={readOnly || savingId === inputKey}
                          onChange={e => onDraftChange(prev => ({ ...prev, [inputKey]: e.target.value }))}
                          onBlur={() => onCommit(inputKey)}
                          onKeyDown={e => { if (e.key === 'Enter') onCommit(inputKey); }}
                        />
                      </Tooltip>
                      <DeltaBadge delta={player.delta} />
                    </div>
                  </div>
                );
              })}
              {!(finance.cols[col.key]?.length) && (
                <div className="mm-finance-empty">Nessun giocatore</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── SEZIONE: INSIGHTS ────────────────────────────────────────────────────────

function PlayerMiniCard({ player, meta, onOpenDetail }) {
  return (
    <div className="mm-mini-card">
      <div className="mm-mini-card-identity">
        <TeamBadge team={player.team} size={24} />
        <div>
          <strong className="mm-mini-card-name">{player.name}</strong>
          <div className="mm-role-chip-group">
            {player.roles.slice(0, 3).map(r => <RoleChip key={r} role={r} size="xs" />)}
          </div>
        </div>
      </div>
      <div className="mm-mini-card-right">
        {meta}
        {onOpenDetail && (
          <button type="button" className="mm-ghost-btn" onClick={() => onOpenDetail(player.id)}>
            Scheda
          </button>
        )}
      </div>
    </div>
  );
}

function InsightsSection({ players, onOpenDetail }) {
  const insights = useMemo(() => buildInsights(players), [players]);

  const cards = [
    {
      title: '🏆 Da blindare',
      sub: 'Maggiore plusvalenza rispetto al prezzo d\'acquisto',
      players: insights.topWinners,
      meta: p => <DeltaBadge delta={p.delta} />,
      empty: 'Nessun giocatore in plusvalenza.',
    },
    {
      title: '⚠️ Da monitorare',
      sub: 'In minusvalenza: valuta una cessione al mercato',
      players: insights.topLosers,
      meta: p => <DeltaBadge delta={p.delta} />,
      empty: 'Nessuna minusvalenza.',
    },
    {
      title: '⚡ Più impatto offensivo',
      sub: 'Gol, assist e rigori nei giocatori di movimento',
      players: insights.bonusLeaders,
      meta: p => <span className="mm-stat-badge">{p.goals}G {p.assists}A</span>,
      empty: 'Nessun dato offensivo.',
    },
    {
      title: '🟨 Più a rischio',
      sub: 'Ammonizioni, espulsioni, autogol',
      players: insights.riskLeaders,
      meta: p => <span className="mm-stat-badge mm-stat-badge--warn">{p.ycards}G {p.rcards}R</span>,
      empty: 'Nessun segnale di rischio.',
    },
    {
      title: '📋 Più presenze',
      sub: 'Giocatori più continui nella stagione in corso',
      players: insights.mostPlayed,
      meta: p => <span className="mm-stat-badge">{p.caps} PG</span>,
      empty: 'Nessun dato presenze.',
    },
  ];

  return (
    <section className="mm-section glass">
      <SectionHead
        title="Segnali di Mercato"
        sub="Analisi rapida su chi blindare, cedere o monitorare."
        badge="Insights"
        icon={<Zap size={14} />}
      />

      <div className="mm-insights-grid">
        {cards.map(card => (
          <div key={card.title} className="mm-insight-card">
            <div className="mm-insight-head">
              <strong>{card.title}</strong>
              <span>{card.sub}</span>
            </div>
            {card.players.length ? (
              <div className="mm-insight-list">
                {card.players.map(p => (
                  <PlayerMiniCard
                    key={p.id}
                    player={p}
                    meta={card.meta(p)}
                    onOpenDetail={onOpenDetail}
                  />
                ))}
              </div>
            ) : (
              <div className="mm-insight-empty">{card.empty}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── SEZIONE: ANALIZZATORE MERCATO ────────────────────────────────────────────

function MarketAnalyzerSection({ players }) {
  const [selectedId, setSelectedId] = useState('');

  const options = useMemo(
    () => [...players].sort((a, b) => b.delta - a.delta),
    [players]
  );

  useEffect(() => {
    if (!options.length) { setSelectedId(''); return; }
    if (!selectedId || !options.find(p => String(p.id) === String(selectedId))) {
      setSelectedId(String(options[0].id));
    }
  }, [options, selectedId]);

  const selected = options.find(p => String(p.id) === String(selectedId)) || options[0];

  const { lostModules, beforePlayable, afterPlayable, impactedRoles } = useMemo(() => {
    if (!selected) return {};
    const before = buildModuleCoverage(players);
    const after  = buildModuleCoverage(players.filter(p => String(p.id) !== String(selected.id)));

    const beforeCounts = {};
    const afterCounts  = {};
    MANTRA_ROLE_ORDER.forEach(r => { beforeCounts[r] = 0; afterCounts[r] = 0; });
    players.forEach(p => p.roles.forEach(r => { if (beforeCounts[r] !== undefined) beforeCounts[r]++; }));
    players.filter(p => String(p.id) !== String(selected.id)).forEach(p => p.roles.forEach(r => { if (afterCounts[r] !== undefined) afterCounts[r]++; }));

    return {
      beforeModules: before,
      afterModules:  after,
      lostModules:   before.filter(m => m.playable && !after.find(am => am.key === m.key)?.playable),
      beforePlayable: before.filter(m => m.playable).length,
      afterPlayable:  after.filter(m => m.playable).length,
      impactedRoles: selected.roles.map(r => ({ role: r, before: beforeCounts[r], after: afterCounts[r] })),
    };
  }, [selected, players]);

  if (!options.length) return null;

  return (
    <section className="mm-section glass">
      <div className="mm-section-head mm-section-head--market">
        <div>
          <h3 className="mm-section-title">Analizzatore Cessione</h3>
          <p className="mm-section-sub">Simula la cessione di un giocatore e misura l'impatto sulla rosa.</p>
        </div>

        <div className="mm-market-select-wrap">
          <label className="mm-market-select-label">Simula cessione di</label>
          <select
            className="mm-market-select"
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
          >
            {options.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} • {p.roleLabel} • {p.delta >= 0 ? '+' : ''}{fmt(p.delta)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selected && (
        <div className="mm-market-body">
          <div className="mm-market-hero">
            <TeamBadge team={selected.team} size={36} />
            <div>
              <strong className="mm-market-hero-name">{selected.name}</strong>
              <div className="mm-role-chip-group">
                {selected.roles.map(r => <RoleChip key={r} role={r} size="xs" />)}
              </div>
            </div>
            <DeltaBadge delta={selected.delta} />
          </div>

          <div className="mm-market-kpis">
            <div><span>Pagato</span><strong>{fmt(selected.buyPrice)} cr</strong></div>
            <div><span>Valore stimato</span><strong>{fmt(selected.valuation)} cr</strong></div>
            <div><span>Crediti liberati</span><strong className="is-positive">{fmt(selected.buyPrice)} cr</strong></div>
            <div><span>PG stagione</span><strong>{selected.caps}</strong></div>
          </div>

          <div className="mm-market-impact">
            <div className="mm-market-impact-card">
              <h4>Moduli giocabili</h4>
              <div className="mm-market-impact-nums">
                <div>
                  <span>Prima</span>
                  <strong>{beforePlayable}</strong>
                </div>
                <div className="mm-market-arrow">→</div>
                <div>
                  <span>Dopo</span>
                  <strong style={{ color: afterPlayable < beforePlayable ? '#f87171' : '#4ade80' }}>
                    {afterPlayable}
                  </strong>
                </div>
              </div>
            </div>

            <div className="mm-market-impact-card">
              <h4>Ruoli impattati</h4>
              <div className="mm-market-roles">
                {impactedRoles?.length ? impactedRoles.map(item => (
                  <div key={item.role} className="mm-market-role-row">
                    <RoleChip role={item.role} size="sm" />
                    <span className={item.after < item.before ? 'is-negative' : ''}>
                      {item.before} → {item.after}
                    </span>
                  </div>
                )) : <span className="mm-muted">Nessun ruolo diretto rilevato.</span>}
              </div>
            </div>
          </div>

          <div className="mm-market-lost">
            <div className="mm-market-lost-head">
              <h4>Moduli che si perderebbero</h4>
              <span>{lostModules?.length || 0}</span>
            </div>
            {lostModules?.length ? (
              <div className="mm-market-lost-list">
                {lostModules.map(m => (
                  <div key={m.key} className="mm-market-lost-item">
                    <strong>{m.label}</strong>
                    <span>non più giocabile</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mm-muted mm-market-lost-ok">
                ✓ Nessun modulo giocabile viene perso con questa cessione.
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ─── EXPORT DEFAULT ───────────────────────────────────────────────────────────

export default function AuctionMantraManageTab({
  auction,
  roster = [],
  players: allPlayers = [],
  purchases = [],
  rolesMode = 'mantra',
  onOpenPlayerDetail,
  onSavePlayerValuation,
  readOnly = false,
  playProbabilityById = {},
}) {
  const [valuationDrafts, setValuationDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  // Inizializza le valutazioni dai dati del roster
  useEffect(() => {
    setValuationDrafts(prev => {
      let changed = false;
      const next = { ...prev };
      (Array.isArray(roster) ? roster : []).forEach(entry => {
        const p = normalizePlayer(entry, {});
        if (!p) return;
        const key = String(p.id);
        if (prev[key] === undefined || prev[key] === null || String(prev[key]).trim() === '') {
          next[key] = p.valuation > 0 ? String(Math.round(p.valuation)) : '';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [roster]);

  const players = useMemo(
    () => (Array.isArray(roster) ? roster : []).map(e => normalizePlayer(e, valuationDrafts)).filter(Boolean),
    [roster, valuationDrafts]
  );

  const handleCommit = useCallback(async (playerId) => {
    if (readOnly || !onSavePlayerValuation) return;
    const key = String(playerId);
    const parsed = Number(valuationDrafts?.[key]);
    if (!Number.isFinite(parsed)) return;
    setSavingId(key);
    try { await onSavePlayerValuation(key, parsed); }
    finally { setSavingId(null); }
  }, [readOnly, onSavePlayerValuation, valuationDrafts]);

  const keeperCount   = players.filter(p => p.isKeeper).length;
  const movementCount = players.length - keeperCount;
  const finance       = useMemo(() => buildFinance(players), [players]);
  const moduleCoverage = useMemo(() => buildModuleCoverage(players, playProbabilityById), [players, playProbabilityById]);
  const playableCount  = moduleCoverage.filter(m => m.playable).length;
  const bestModule     = moduleCoverage[0];

  if (!players.length) {
    return (
      <main className="mm-root">
        <div className="mm-empty-state">
          <Users size={40} />
          <h3>Nessun giocatore in rosa</h3>
          <p>Acquista dei giocatori all'asta per vedere l'analisi della rosa Mantra.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mm-root">

      {/* ── KPI Panoramica ── */}
      <div className="mm-kpi-strip">
        <div className="mm-kpi">
          <span>Giocatori</span>
          <strong>{players.length}</strong>
        </div>
        <div className="mm-kpi">
          <span>Portieri</span>
          <strong>{keeperCount}</strong>
        </div>
        <div className="mm-kpi">
          <span>Movimento</span>
          <strong>{movementCount}</strong>
        </div>
        <div className="mm-kpi mm-kpi--accent">
          <span>Moduli giocabili</span>
          <strong>{playableCount} / {MANTRA_PRESET_COUNT}</strong>
        </div>
        <div className="mm-kpi">
          <span>Miglior modulo</span>
          <strong>{bestModule ? bestModule.label : '—'}</strong>
        </div>
        <div className={`mm-kpi ${finance.delta >= 0 ? 'mm-kpi--pos' : 'mm-kpi--neg'}`}>
          <span>Saldo plusvalenze</span>
          <strong>{finance.delta >= 0 ? '+' : ''}{fmt(finance.delta)} cr</strong>
        </div>
      </div>

      <AlertsDashboardSection players={players} moduleCoverage={moduleCoverage} />
      <RoleCoverageSection players={players} />
      <ModuleCoverageSection players={players} playProbabilityById={playProbabilityById} />
      <CompatSection players={players} />
      <InsightsSection players={players} onOpenDetail={onOpenPlayerDetail} />
      <FinanceSection
        roster={roster}
        valuationDrafts={valuationDrafts}
        onDraftChange={setValuationDrafts}
        onCommit={handleCommit}
        savingId={savingId}
        readOnly={readOnly}
      />
      <MarketAnalyzerSection players={players} />

    </main>
  );
}
